import { observable, action, computed } from "mobx";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import widgetDataStore from "./widgetDataStore";

// makeApi resolves both success and error responses (it never throws) - on error, the body
// we want lives under res.response.data instead of res.data.
const getApiData = res => res?.response?.data || res?.data;

class DashboardV2Store {
  @observable dashboards = [];
  @observable activeDashboardId = "";
  @observable widgetCatalog = [];
  @observable isLoadingDashboards = false;
  @observable isLoadingCatalog = false;
  @observable showWidgetCatalog = false;
  @observable addingWidgetId = null;
  @observable isCreatingDashboard = false;

  @computed
  get activeDashboard() {
    return this.dashboards.find(d => d.id === this.activeDashboardId) || null;
  }

  @action
  setActiveDashboardId = id => {
    this.activeDashboardId = id;
  };

  @action
  setShowWidgetCatalog = value => {
    this.showWidgetCatalog = value;
  };

  @action
  fetchDashboards = async () => {
    this.isLoadingDashboards = true;
    try {
      const res = await makeApi({ url: URLS.getDashboards, method: "GET" });
      const data = res?.data;
      if (data?.status === "failed") {
        toasterState.setToastMsg("Failed to load dashboards", "fail");
        return;
      }
      const list = data?.dashboards;
      if (list?.length) {
        const sorted = [...list].sort((a, b) => a.order - b.order);
        this.dashboards = sorted;
        if (!this.activeDashboardId || !sorted.find(d => d.id === this.activeDashboardId)) {
          this.activeDashboardId = sorted[0]?.id || "";
        }
      } else {
        this.dashboards = [];
      }
    } catch (err) {
      console.error("DashboardV2: fetchDashboards error", err);
      toasterState.setToastMsg("Failed to load dashboards", "fail");
    } finally {
      this.isLoadingDashboards = false;
    }
  };

  @action
  fetchWidgetCatalog = async () => {
    this.isLoadingCatalog = true;
    try {
      const res = await makeApi({ url: URLS.getWidgetCatalog, method: "GET" });
      const data = res?.data;
      if (data?.status === "failed") {
        toasterState.setToastMsg("Failed to load widget catalog", "fail");
        return;
      }
      const list = data?.widgets;
      if (list?.length) {
        this.widgetCatalog = list.filter(w => w.enabled);
      }
    } catch (err) {
      console.error("DashboardV2: fetchWidgetCatalog error", err);
      toasterState.setToastMsg("Failed to load widget catalog", "fail");
    } finally {
      this.isLoadingCatalog = false;
    }
  };

  @action
  createDashboard = async name => {
    this.isCreatingDashboard = true;
    try {
      const res = await makeApi({
        url: URLS.createDashboard,
        method: "POST",
        data: { name }
      });
      const data = getApiData(res);
      if (data?.status === "failed") {
        const code = data?.responseCode;
        if (code === 101) {
          toasterState.setToastMsg(data?.error || "Dashboard already exists", "fail");
        } else if (code === 102) {
          toasterState.setToastMsg("Dashboard limit reached. You can create a maximum of 10 dashboards", "fail");
        } else {
          toasterState.setToastMsg(data?.error || "Failed to create dashboard", "fail");
        }
        return null;
      }
      const created = data?.dashboard;
      if (created?.id) {
        const sorted = [...this.dashboards, created].sort((a, b) => a.order - b.order);
        this.dashboards = sorted;
        this.activeDashboardId = created.id;
        toasterState.setToastMsg("Dashboard created successfully", "success");
      }
      return created;
    } catch (err) {
      console.error("DashboardV2: createDashboard error", err);
      toasterState.setToastMsg("Failed to create dashboard", "fail");
    } finally {
      this.isCreatingDashboard = false;
    }
  };

  @action
  renameDashboard = async (id, name) => {
    try {
      const res = await makeApi({
        url: URLS.renameDashboard,
        method: "PUT",
        data: { id, name }
      });
      const data = getApiData(res);
      if (data?.status === "failed") {
        const code = data?.responseCode;
        const message = code === 101 ? data?.error || "Dashboard already exists" : data?.error || "Failed to rename dashboard";
        toasterState.setToastMsg(message, "fail");
        return false;
      }
      const updated = data?.dashboard;
      if (updated?.id) {
        this.dashboards = this.dashboards.map(d => (d.id === id ? { ...d, name: updated.name } : d));
        toasterState.setToastMsg("Dashboard renamed", "success");
        return true;
      }
      return false;
    } catch (err) {
      console.error("DashboardV2: renameDashboard error", err);
      toasterState.setToastMsg("Failed to rename dashboard", "fail");
      return false;
    }
  };

  @action
  deleteDashboard = async id => {
    try {
      const res = await makeApi({
        url: `${URLS.deleteDashboard}?id=${id}`,
        method: "DELETE"
      });
      if (res?.data?.status === "failed") {
        toasterState.setToastMsg(res?.data?.error || "Failed to delete dashboard", "fail");
        return;
      }
      const remaining = this.dashboards.filter(d => d.id !== id);
      this.dashboards = remaining;
      if (this.activeDashboardId === id) {
        this.activeDashboardId = remaining[0]?.id || "";
      }
      toasterState.setToastMsg("Dashboard deleted", "success");
    } catch (err) {
      console.error("DashboardV2: deleteDashboard error", err);
      toasterState.setToastMsg("Failed to delete dashboard", "fail");
    }
  };

  @action
  addWidget = async (widgetId, category) => {
    const dashboard = this.activeDashboard;
    if (!dashboard || this.addingWidgetId) return;
    const alreadyAdded = dashboard.widgets.some(w => w.widgetId === widgetId);
    if (alreadyAdded) return;

    this.addingWidgetId = widgetId;
    try {
      const newWidget = {
        widgetId,
        category,
        position: dashboard.widgets.length,
        pinned: false,
        pinnedOrder: -1
      };
      const updatedWidgets = [...dashboard.widgets, newWidget];
      const saved = await this.saveWidgets(dashboard.id, updatedWidgets);
      if (saved) {
        widgetDataStore.fetchSingleWidget(newWidget);
        toasterState.setToastMsg("Widget added", "success");
      }
    } finally {
      this.addingWidgetId = null;
    }
  };

  @action
  togglePin = async widgetId => {
    const dashboard = this.activeDashboard;
    if (!dashboard) return;

    const widget = dashboard.widgets.find(w => w.widgetId === widgetId);
    if (!widget) return;

    const pinnedWidgets = dashboard.widgets.filter(w => w.pinned);

    if (!widget.pinned && pinnedWidgets.length >= 3) {
      toasterState.setToastMsg("Maximum 3 widgets can be pinned. Unpin one to add another.", "fail");
      return;
    }

    let updatedWidgets;
    if (widget.pinned) {
      // Unpin — compact pinnedOrder values for remaining pinned widgets
      const newPinnedOrder = {};
      dashboard.widgets
        .filter(w => w.widgetId !== widgetId && w.pinned)
        .sort((a, b) => a.pinnedOrder - b.pinnedOrder)
        .forEach((w, idx) => { newPinnedOrder[w.widgetId] = idx; });

      updatedWidgets = dashboard.widgets.map(w => {
        if (w.widgetId === widgetId) return { ...w, pinned: false, pinnedOrder: -1 };
        if (newPinnedOrder[w.widgetId] != null) return { ...w, pinnedOrder: newPinnedOrder[w.widgetId] };
        return w;
      });
    } else {
      const nextOrder = pinnedWidgets.length;
      updatedWidgets = dashboard.widgets.map(w =>
        w.widgetId === widgetId ? { ...w, pinned: true, pinnedOrder: nextOrder } : w
      );
    }

    const saved = await this.saveWidgets(dashboard.id, updatedWidgets);
    if (saved) {
      toasterState.setToastMsg(
        widget.pinned ? "Widget unpinned successfully" : "Widget pinned successfully",
        "success"
      );
    }
  };

  @action
  reorderWidgets = async newUnpinnedWidgets => {
    const dashboard = this.activeDashboard;
    if (!dashboard) return;
    const pinnedWidgets = dashboard.widgets.filter(w => w.pinned);
    const updatedWidgets = [...pinnedWidgets, ...newUnpinnedWidgets].map((w, idx) => ({
      ...w,
      position: idx
    }));
    const saved = await this.saveWidgets(dashboard.id, updatedWidgets);
    if (saved) {
      toasterState.setToastMsg("Widget order updated", "success");
    }
  };

  @action
  reorderPinnedWidgets = async newPinnedWidgets => {
    const dashboard = this.activeDashboard;
    if (!dashboard) return;
    const unpinnedWidgets = dashboard.widgets.filter(w => !w.pinned);
    const updatedPinned = newPinnedWidgets.map((w, idx) => ({ ...w, pinnedOrder: idx }));
    const updatedWidgets = [...updatedPinned, ...unpinnedWidgets].map((w, idx) => ({
      ...w,
      position: idx
    }));
    const saved = await this.saveWidgets(dashboard.id, updatedWidgets);
    if (saved) {
      toasterState.setToastMsg("Widget order updated", "success");
    }
  };

  @action
  removeWidget = async widgetId => {
    const dashboard = this.activeDashboard;
    if (!dashboard) return;
    const updatedWidgets = dashboard.widgets
      .filter(w => w.widgetId !== widgetId)
      .map((w, idx) => ({ ...w, position: idx }));
    const saved = await this.saveWidgets(dashboard.id, updatedWidgets);
    if (saved) {
      widgetDataStore.clearWidgetData(widgetId);
      toasterState.setToastMsg("Widget removed", "success");
    }
  };

  saveWidgets = async (dashboardId, widgets) => {
    try {
      const res = await makeApi({
        url: URLS.updateDashboard,
        method: "PUT",
        data: { id: dashboardId, widgets }
      });
      const data = res?.data;
      if (data?.status === "failed") {
        const code = data?.responseCode;
        if (code === 103) {
          toasterState.setToastMsg("Maximum 3 widgets can be pinned. Unpin one to add another", "fail");
        } else {
          toasterState.setToastMsg(data?.error || "Failed to update dashboard", "fail");
        }
        return false;
      }
      if (data) {
        this.dashboards = this.dashboards.map(d =>
          d.id === dashboardId ? { ...d, widgets } : d
        );
        return true;
      }
      return false;
    } catch (err) {
      console.error("DashboardV2: saveWidgets error", err);
      toasterState.setToastMsg("Failed to update dashboard", "fail");
      return false;
    }
  };
}

const dashboardV2Store = new DashboardV2Store();
export { dashboardV2Store };
export default dashboardV2Store;
